import type { ScaleLinear } from "d3";
import { PureComponent } from "react";
import { connect } from "react-redux";

import type { IRasterSegment } from "../../../app-utils/rasterization-service.js";
import type {
  Contig,
  FullPileupAlignment,
  PileupAlignment,
  PileupAlignmentSegment,
  Range,
  Track,
  TrackNgsOrderedBamFilesData,
} from "../../../graphql.js";
import { type ITrackData, getTrackId } from "../../../redux/modules/genome-browser.js";
import type { IAppState } from "../../../redux/reducer.js";
import { DEFAULT_TRACK_HEIGHT, NGS_READS_PILEUP_DENSITY, TRACK_SUBTYPE } from "../../../settings/settings.js";
import type { SetTooltipContent } from "../GenomeViewerTracksList.js";
import { NgsReadTrack } from "./NGSReadsTrack.js";
import { NGS_LANES_GAP, NGS_READS_SHAPE_HEIGHT } from "./_mod.js";

export interface INgsReadsTrackData extends ITrackData {
  ngsPileupDataLength?: number; // just for calculating the total height from the parent component perspective (no need to store this data in redux)
}

// TODO Let's make that generic?
export interface ITrackTooltip<T> {
  isLoading: boolean;
  uid: string | null;
  data: T | null;
}

export interface INgsReadsTooltip {
  index: number | null; // multi purpose index dependant of the segment.type (.match, .insertion, .deletion)
  alignment: (Partial<FullPileupAlignment> & Partial<PileupAlignment>) | null;
  segment: PileupAlignmentSegment | null;
}

export const INITIAL_NGS_READS_TRACK_DATA: Partial<INgsReadsTrackData> = {
  ngsPileupDataLength: null,
};

interface INgsReadsTrackLoaderOwnProps {
  track: Track; // track spec
  bamFileId: number;
  scale: ScaleLinear<number, number>;
  setTooltipContent: SetTooltipContent;
}

interface INgsReadsTrackLoaderAppStateProps {
  trackData: INgsReadsTrackData;
  range: Range;
  contig: Contig;
}

interface INgsReadsTrackLoaderDispatchProps {}

interface INgsCoverageTrackLoaderProps
  extends INgsReadsTrackLoaderAppStateProps,
    INgsReadsTrackLoaderDispatchProps,
    INgsReadsTrackLoaderOwnProps {}

function mapStateToProps(state: IAppState, ownProps: INgsReadsTrackLoaderOwnProps): INgsReadsTrackLoaderAppStateProps {
  const trackId: string = getTrackId({
    ...ownProps.track,
    fileId: ownProps.bamFileId,
    trackSubtype: TRACK_SUBTYPE.NGS_PILEUP,
  });

  return {
    range: state.genomeBrowser.range,
    contig: state.genomeBrowser.contig,
    trackData: state.genomeBrowser.trackDataMap[trackId] as INgsReadsTrackData,
  };
}

function mapDispatchToProps(_dispatch): INgsReadsTrackLoaderDispatchProps {
  return {};
}

interface INgsReadsTrackLoaderState {
  rasterData: IRasterSegment[];
}

class NgsReadsTrackLoader extends PureComponent<INgsCoverageTrackLoaderProps, INgsReadsTrackLoaderState> {
  getTrackHeight = (): number => {
    const bamFileConfig: TrackNgsOrderedBamFilesData =
      this.props.track.trackCustomization?.ngsConfiguration.orderedBamFiles.find(
        (bamFileConfig) => bamFileConfig.bamFileId === this.props.bamFileId,
      );
    const density =
      (bamFileConfig.pileupDensity as keyof typeof NGS_READS_PILEUP_DENSITY) || NGS_READS_PILEUP_DENSITY.MEDIUM;
    let height = 0;
    if (bamFileConfig) {
      const shapeHeight = NGS_READS_SHAPE_HEIGHT[bamFileConfig.pileupDensity as NGS_READS_PILEUP_DENSITY];
      height =
        Math.min(bamFileConfig.pileupLanesCount, this.props.trackData.ngsPileupDataLength || 0) *
          (shapeHeight + NGS_LANES_GAP[density]) -
        NGS_LANES_GAP[density];
    }
    return Math.max(DEFAULT_TRACK_HEIGHT, height);
  };

  render() {
    const readsTrackId: string = getTrackId({
      ...this.props.track,
      fileId: this.props.bamFileId,
      trackSubtype: TRACK_SUBTYPE.NGS_PILEUP,
    });

    return (
      <NgsReadTrack
        trackId={readsTrackId}
        trackName={this.props.track.name}
        bamFileId={this.props.bamFileId}
        trackHeight={this.getTrackHeight()}
        setTooltipContent={this.props.setTooltipContent}
      />
    );
  }
}

const SmartNgsReadsTrackLoader = connect<
  INgsReadsTrackLoaderAppStateProps,
  INgsReadsTrackLoaderDispatchProps,
  INgsReadsTrackLoaderOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(NgsReadsTrackLoader);

export { SmartNgsReadsTrackLoader as NgsReadsTrackLoader };
